












import { computed, defineComponent, onMounted, ref, useContext, useFetch, useRoute, watch } from '@nuxtjs/composition-api';
import { SfLoader } from '@storefront-ui/vue';
import { CacheTagPrefix, useCache } from '@vue-storefront/cache';
import { merge } from 'lodash-es';
import DynamicNavigation from '~/components/Navigation/DynamicNavigation.vue';
import AnalysisPackagesSection from '~/components/sections/AnalysisPackagesSection.vue';
import LoadWhenVisible from '~/components/utils/LoadWhenVisible.vue';
import { useApi, useWishlist } from '~/composables';
import { getMetaInfo } from '~/helpers/getMetaInfo';
import type { ProductDetailsQuery } from '~/modules/GraphQL/types';
import ProductSkeleton from '~/modules/catalog/product/components/loading/ProductSkeleton.vue';
import { useProduct } from '~/modules/catalog/product/composables/useProduct';
import { ProductTypeEnum } from '~/modules/catalog/product/enums/ProductTypeEnum';
import { getName as getProductName } from '~/modules/catalog/product/getters/productGetters';
import getProductPriceBySkuGql from '~/modules/catalog/product/queries/getProductPriceBySku.gql';
import { usePageStore } from '~/stores/page';
import VirtualProduct from '../product/components/product-types/virtual/VirtualProduct.vue';
import { Breadcrumb, CustomProduct } from '../types';

export default defineComponent({
  name: 'ProductPage',
  components: {
    ProductSkeleton,
    VirtualProduct,
    LoadWhenVisible,
    SfLoader,
    DynamicNavigation,
    AnalysisPackagesSection,
  },
  middleware: ['package'],
  setup() {
    const { routeData } = usePageStore();
    const { query } = useApi();
    const product = ref<CustomProduct | null>(null);
    const { addTags } = useCache();
    const route = useRoute();
    const isFetchingProductBaseData = ref(false);

    const { getProductDetails, loading } = useProduct();
    const { error: nuxtError, localePath, i18n } = useContext();
    const { load: loadWishlist } = useWishlist();

    const breadcrumbs = ref<Breadcrumb[]>([
      {
        text: i18n.t('Home') as string,
        link: localePath('/'),
      },
      {
        text: i18n.t('Analysis packages') as string,
        link: localePath('/provanalyser'),
      },
      {
        text: '',
        link: route.value.path,
      },
    ]);

    const getBaseSearchQuery = () => ({
      filter: {
        sku: {
          eq: routeData.sku,
        },
      },
      configurations: Object.entries(route.value.query)
        .filter((config) => config[0] !== 'wishlist')
        .map((config) => config[1]) as string[],
    });

    // eslint-disable-next-line no-underscore-dangle
    const renderer = computed(() => product.value?.__typename ?? ProductTypeEnum.SIMPLE_PRODUCT);

    const fetchProductBaseData = async (searchQuery = getBaseSearchQuery()) => {
      isFetchingProductBaseData.value = true;
      const result = await getProductDetails(searchQuery);
      product.value = result.items?.[0] as CustomProduct;
      isFetchingProductBaseData.value = false;
    };

    const fetchProductExtendedData = async (searchQuery = getBaseSearchQuery()) => {
      const { data } = await query<ProductDetailsQuery>(getProductPriceBySkuGql, searchQuery);

      product.value = merge({}, product.value, data.products?.items?.[0] as CustomProduct);
    };

    useFetch(async () => {
      await fetchProductBaseData();
      breadcrumbs.value[breadcrumbs.value.length - 1].text = getProductName(product.value);

      if (Boolean(product?.value?.sku) === false) nuxtError({ statusCode: 404 });

      const tags = [
        {
          prefix: CacheTagPrefix.View,
          value: `product-${routeData.sku}`,
        },
      ];

      const productTags = [
        {
          prefix: CacheTagPrefix.Product,
          value: product.value.uid,
        },
      ];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      addTags([...tags, ...productTags]);
    });

    onMounted(async () => {
      await loadWishlist();
    });

    // Price is fetched separately to not cache it.
    // We don't do it in useMounted because product.value is an observer at that point and we cannot merge the objects.
    watch(
      isFetchingProductBaseData,
      (newValue) => {
        if (newValue) return;
        fetchProductExtendedData();
      },
      { immediate: true },
    );

    return {
      renderer,
      loading,
      product,
      breadcrumbs,
      fetchProduct: fetchProductExtendedData,
    };
  },
  head() {
    return getMetaInfo(this.product);
  },
});
